import { Box, MenuItem, Pagination, PaginationItem, Select, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/system';
import {
  useGridApiContext,
  useGridSelector,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  GridFooterContainer,
  type GridSlotProps,
  gridPageCountSelector,
} from '@mui/x-data-grid';
import type { DataGridProps } from '@mui/x-data-grid';
import { gridPaginationModelSelector } from '@mui/x-data-grid/hooks/features/pagination/gridPaginationSelector';
import classNames from 'classnames';
import { FC, useMemo } from 'react';

import { useTranslations } from '../../../hooks/use-translations';
import { DownArrowIcon } from '../../icons';

import styles from './TableFooter.module.scss';

type TableFooterTotalRowProps = {
  sumColumns: Array<{
    field: string;
  }>;
};

export type TableFooterProps = GridSlotProps['footer'] & {
  pagination?: DataGridProps['pagination'];
  pageSizeOptions?: number[];
  hideTotalRow?: boolean;
  hideFooter?: boolean;
  totalRowProps?: TableFooterTotalRowProps;
};

export const TableFooter: FC<TableFooterProps> = (props) => {
  const { pagination, pageSizeOptions, hideTotalRow, totalRowProps, hideFooter } = props;

  const t = useTranslations('table');
  const apiRef = useGridApiContext();
  const { page, pageSize } = useGridSelector(apiRef, gridPaginationModelSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const isSmallSize = useMediaQuery('(max-width:960px)');

  if (!(hideTotalRow === true) && totalRowProps === undefined) {
    throw new Error('totalRowProps not set');
  }

  return (
    <>
      {!(hideTotalRow === true) && <TableFooterTotalRow {...(totalRowProps as TableFooterTotalRowProps)} />}
      {!(hideFooter === true) && (
        <GridFooterContainer className={styles.GridFooterContainerRoot}>
          <Stack direction={isSmallSize ? 'column' : 'row'} spacing={4} alignContent="center" justifyContent="center">
            <Box sx={{ marginLeft: { xs: '0', md: '16px' }, lineHeight: { xs: '20px', md: '52px' } }}>
              {t('Page number', { pageNumber: page + 1, totalPage: pageCount })}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'space-between', md: 'center' },
              }}
            >
              {pageSizeOptions !== undefined && (
                <>
                  <span>{t('On the page')}</span>
                  <Select
                    value={pageSize}
                    color="secondary"
                    sx={{ marginTop: 0, marginLeft: 1.5, height: '32px' }}
                    onChange={(val) => {
                      apiRef.current.setPageSize(Number(val.target.value));
                    }}
                  >
                    {pageSizeOptions.map((count) => (
                      <MenuItem value={count} key={count}>
                        {count}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
            </Box>
          </Stack>

          {pagination && (
            <Pagination
              variant="outlined"
              shape="rounded"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  slots={{
                    previous: (props) => (
                      <DownArrowIcon {...props} className={classNames(styles.leftArrow, props.className)} />
                    ),
                    next: (props) => (
                      <DownArrowIcon {...props} className={classNames(styles.rightArrow, props.className)} />
                    ),
                  }}
                />
              )}
              color="primary"
              boundaryCount={2}
              count={pageCount}
              page={page + 1}
              onChange={(event, value) => apiRef.current.setPage(value - 1)}
              sx={{ marginTop: { xs: '2rem', md: '0' } }}
            />
          )}
        </GridFooterContainer>
      )}
    </>
  );
};

const TableFooterTotalRow: FC<TableFooterTotalRowProps> = ({ sumColumns }) => {
  const theme = useTheme();
  const apiRef = useGridApiContext();

  const rowIds = apiRef.current.getAllRowIds();
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const totalColumns = useMemo(() => {
    if (rowIds.length === 0) return false;

    const totalValues = rowIds
      .map((rowId, index) => {
        /*
         Нужно брать именно копию значения. Потому что тут возвращается ссылка.
         и если в ней менять значения, то происходит изменение данных в таблице
        */
        return { ...apiRef.current.getRow(rowId) };
      })
      .reduce((prev, current) => {
        sumColumns.forEach((col) => {
          // if (typeof current[col.field] !== 'number') {
          //   throw Error(`Can't calculate total sum for field '${col.field}'. Type of value must be number.`);
          // }

          if (isNaN(Number(current[col.field]))) {
            current[col.field] = 0;
          }

          if (prev[col.field] === undefined) {
            prev[col.field] = 0;
          }

          const prevVal: number = prev[col.field] ?? 0;
          const currVal: number = current[col.field] ?? 0;
          prev[col.field] = prevVal + currVal;
        });

        return prev;
      });

    return (
      <>
        {columns
          // формируем необходимые для обработки данные (нам важно не потерять порядковый номер колонки)
          .map((col, index) => ({ field: col.field, index }))
          // Убираем колонки которые не отображены
          .filter((col) => columnVisibilityModel[col.field])
          // Формируем колонки в Total Row
          .map((col, i) => {
            /*
                Хак как достать ширину колонок: https://github.com/mui/mui-x/blob/master/packages/grid/x-data-grid/src/components/GridRow.tsx#L440
                */
            const cellColSpanInfo = apiRef.current.unstable_getCellColSpanInfo(rowIds[0], col.index) as {
              spannedByColSpan: boolean;
              cellProps: { width: number };
            };

            if (cellColSpanInfo?.spannedByColSpan) {
              throw new Error(`Can't calculate column width (field: ${col.field})`);
            }

            const { width } = cellColSpanInfo?.cellProps ?? { width: 0 };
            let value = totalValues[col.field];
            // Если в колонке не нужно сумировать
            if (sumColumns.findIndex((sc) => sc.field === col.field) === -1) value = '';
            // В 1-й колонке всегда Total
            if (i === 0) value = 'Total';

            return (
              <div style={{ width, padding: '0 10px' }} key={col.index}>
                {' '}
                <Typography sx={{ fontWeight: 700 }}>{value} </Typography>
              </div>
            );
          })}
      </>
    );
  }, [rowIds, columns, apiRef, sumColumns, columnVisibilityModel]);

  if (totalColumns === false) {
    return null;
  }

  return (
    <Box
      sx={{
        color: theme.palette.text.primary,
        padding: '10px 0',
        display: 'flex',
        borderRadius: '0 0 6px 6px',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {totalColumns}
    </Box>
  );
};
